import type {
  OnboardingSelection,
  OnboardingStep,
} from '../lib/onboarding';

interface QuickRepliesProps {
  step: OnboardingStep;
  onSelect: (selection: OnboardingSelection) => void;
}

/**
 * Chip-based reply UI shown in place of ChatInput during onboarding.
 *
 *   compound — chips fire onSelect(structuredValue) on tap
 *
 * Only `compound` mode is rendered here. The `dates` and `text` modes
 * are handled by `OnboardingDateStep` and `OnboardingTextStep` directly
 * in MePage; QuickReplies returns null defensively if those modes ever
 * reach it (shouldn't happen — MePage routes them to their own
 * components — but the early return keeps TypeScript's exhaustiveness
 * check satisfied).
 *
 * The `multi` mode was removed 3 Jun 2026 in the allergens descope
 * (API_CONTRACT.md v8). If a future onboarding step needs multi-select
 * chips, reintroduce the variant in `src/lib/onboarding.ts` and add a
 * matching branch below.
 */
export function QuickReplies({ step, onSelect }: QuickRepliesProps) {
  if (step.mode !== 'compound') return null;

  return (
    <div
      role="group"
      aria-label="Reply options"
      className="flex flex-wrap gap-2 px-3 py-3"
    >
      {step.options.map((opt) => (
        <button
          key={`${opt.value.party}-${opt.value.hasDog ? 'dog' : 'nodog'}`}
          type="button"
          onClick={() => onSelect(opt.value)}
          className="rounded-full bg-cream-100 px-4 py-2 text-sm font-medium text-moor-900 ring-1 ring-moor-200 transition-colors hover:bg-moor-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-moor-500/40 focus-visible:ring-offset-2"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}
